import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { MetricType } from "@/lib/health/healthMap";
import { readBodyHealthRecordGuards, withBodyHealthRecordGuards } from '@/lib/bodyHealthRecordGuards';
import { BODY_HEALTH_CHANGED_EVENT, isBodyHealthChangeFor } from '@/lib/bodyHealthChanges';
import { readHealthMetricHistory, type HealthHistoryWindow, type HealthMetricRow } from '@/lib/health/history';
export type { HealthMetricRow } from '@/lib/health/history';

export function useHealthMetrics() {
  const [{ metrics, historyWindow }, setHistory] = useState<{ metrics: HealthMetricRow[]; historyWindow: HealthHistoryWindow | null }>({ metrics: [], historyWindow: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [identityGeneration, setIdentityGeneration] = useState(0);
  const owner = useRef<string | null>(null);
  const epoch = useRef(0);
  const version = useRef(0);
  const mounted = useRef(false);
  const request = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    const run = ++version.current;
    request.current?.abort();
    const controller = new AbortController(); request.current = controller;
    const current = () => mounted.current && version.current === run;
    const timer = window.setTimeout(() => {
      controller.abort();
      if (current()) { version.current++; setError(true); setLoading(false); }
    }, 12000);
    setLoading(true);
    try {
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (!current()) return;
      if (authError) throw authError;
      if (!session) {
        if (owner.current !== null) { owner.current = null; setIdentityGeneration(++epoch.current); setUserId(null); setHistory({ metrics: [], historyWindow: null }); }
        throw new Error("Not authenticated");
      }
      const id = session.user.id;
      if (owner.current !== id) { owner.current = id; setIdentityGeneration(++epoch.current); setUserId(id); setHistory({ metrics: [], historyWindow: null }); }
      const assertCurrent = () => { if (!current() || owner.current !== id) throw new Error('Health session changed'); };
      const history = await readHealthMetricHistory({ client: supabase, ownerId: id, bearer: session.access_token, signal: controller.signal, assertCurrent });
      const guards = await readBodyHealthRecordGuards(id, 'health', history.rows.map(row => row.id), {
        signal: controller.signal, bearer: session.access_token,
        assertCurrent,
      });
      assertCurrent();
      setHistory({ metrics: withBodyHealthRecordGuards(history.rows, guards), historyWindow: history.window }); setError(false);
    } catch (cause) {
      if (current()) { console.error("[useHealthMetrics] read failed", cause); setError(true); }
    } finally {
      window.clearTimeout(timer);
      if (current()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    const lifecycleVersion = version;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (next === owner.current) return;
      version.current++; request.current?.abort(); owner.current = next;
      setIdentityGeneration(++epoch.current);
      setUserId(next); setHistory({ metrics: [], historyWindow: null }); setError(false); setLoading(!!next);
      if (next) queueMicrotask(() => { if (mounted.current) void refresh(); });
    });
    void refresh();
    return () => { mounted.current = false; lifecycleVersion.current++; request.current?.abort(); subscription.unsubscribe(); };
  }, [refresh]);
  useEffect(() => {
    const changed = (event: Event) => { if (isBodyHealthChangeFor(event, owner.current, 'health')) void refresh(); };
    window.addEventListener(BODY_HEALTH_CHANGED_EVENT, changed);
    return () => window.removeEventListener(BODY_HEALTH_CHANGED_EVENT, changed);
  }, [refresh]);
  // Old UI callbacks cannot bypass the review, durable operation and receipt contract.
  const updateMetric = useCallback(async (_id: string, _value: number): Promise<never> => { throw new Error('body_health_review_required'); }, []);
  const deleteMetric = useCallback(async (_row: HealthMetricRow): Promise<never> => { throw new Error('body_health_review_required'); }, []);

  const latestOf = useCallback((type: MetricType | string) => metrics.find(row => row.metric_type === type) ?? null, [metrics]);
  const capture = useCallback(() => {
    const generation = identityGeneration;
    return () => mounted.current && !!userId && owner.current === userId && epoch.current === generation;
  }, [userId, identityGeneration]);
  return { metrics, historyWindow, userId, identityGeneration, capture, loading, error, refresh, updateMetric, deleteMetric, latestOf };
}
